import React from 'react';
import { Heart, Users, Globe, HandHeart, Home, Utensils, GraduationCap, Baby } from 'lucide-react';

const OutreachPage = () => {
  const gospelOutreach = [
    {
      title: 'Digital Evangelism',
      description: 'Reaching souls worldwide through online platforms, social media ministry, and digital gospel sharing',
      icon: Globe,
      activities: ['Online Gospel presentations', 'Social media evangelism', 'Digital tract distribution', 'Virtual altar calls']
    },
    {
      title: 'Personal Witness Training',
      description: 'Equipping believers to share their faith effectively in their daily interactions and relationships',
      icon: Users,
      activities: ['Evangelism workshops', 'Personal testimony training', 'Conversation starters', 'Follow-up discipleship']
    }
  ];

  const communityOutreach = [
    {
      title: 'Homeless Ministry',
      description: 'Providing meals, clothing, and the Gospel to our homeless neighbors with dignity and love',
      icon: Home,
      image: 'https://images.unsplash.com/photo-1488521787991-ed7bbaae773c?q=80&w=400&h=250&auto=format&fit=crop',
      needs: ['Volunteers for meal service', 'Clothing donations', 'Hygiene supplies', 'Prayer support']
    },
    {
      title: 'Food Pantry',
      description: 'Addressing food insecurity in our community by providing groceries and nutritious meals to families in need',
      icon: Utensils,
      image: 'https://images.unsplash.com/photo-1593113598332-cd288d649433?q=80&w=400&h=250&auto=format&fit=crop',
      needs: ['Non-perishable food items', 'Fresh produce donations', 'Volunteer sorting teams', 'Delivery drivers']
    },
    {
      title: 'Educational Support',
      description: 'Supporting underprivileged children and families with tutoring, school supplies, and mentorship programs',
      icon: GraduationCap,
      image: 'https://images.unsplash.com/photo-1497486751825-1233686d5d80?q=80&w=400&h=250&auto=format&fit=crop',
      needs: ['Tutoring volunteers', 'School supplies', 'Technology donations', 'Mentorship programs']
    },
    {
      title: 'Single Parent Support',
      description: 'Providing practical assistance, childcare, and spiritual encouragement to single parent families',
      icon: Baby,
      image: 'https://images.unsplash.com/photo-1559662780-33dc2d48a8d8?q=80&w=400&h=250&auto=format&fit=crop',
      needs: ['Childcare volunteers', 'Meal preparation', 'Transportation assistance', 'Emotional support']
    }
  ];

  return (
    <div className="pt-16 lg:pt-20 pb-20 lg:pb-0">
      {/* Hero Section */}
      <section className="py-12 lg:py-20 bg-gradient-to-br from-orange-50 to-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-8 lg:mb-12">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-800 mb-6 font-display">
              Gospel & Community Outreach
            </h1>
            <p className="text-lg lg:text-xl text-gray-600 max-w-4xl mx-auto font-sans">
              Following Christ's example of love in action, we share the Gospel and serve 
              the underserved in our community with compassion, dignity, and practical support.
            </p>
          </div>
        </div>
      </section>

      {/* Gospel Outreach */}
      <section className="py-12 lg:py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-6 font-display">
              Gospel Outreach
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto font-sans">
              "Go therefore and make disciples of all nations" - Matthew 28:19
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {gospelOutreach.map((ministry, index) => {
              const IconComponent = ministry.icon;
              return (
                <div key={index} className="bg-white rounded-xl shadow-lg p-6 lg:p-8 hover:shadow-xl transition-shadow duration-300">
                  <div className="flex items-center space-x-4 mb-6">
                    <div className="w-16 h-16 bg-[#f17b21] rounded-full flex items-center justify-center">
                      <IconComponent className="w-8 h-8 text-white" />
                    </div>
                    <h3 className="text-xl lg:text-2xl font-bold text-gray-800 font-display">{ministry.title}</h3>
                  </div>
                  
                  <p className="text-gray-600 mb-6 leading-relaxed font-sans">
                    {ministry.description}
                  </p>

                  <div>
                    <h4 className="font-semibold text-gray-800 mb-4 font-sans">Current Activities:</h4>
                    <ul className="space-y-2">
                      {ministry.activities.map((activity, actIndex) => (
                        <li key={actIndex} className="flex items-start">
                          <span className="w-2 h-2 bg-[#f17b21] rounded-full mt-2 mr-3 flex-shrink-0"></span>
                          <span className="text-gray-600 font-sans text-sm lg:text-base">{activity}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Community Outreach */}
      <section className="py-12 lg:py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-6 font-display">
              Local Community Outreach
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto font-sans">
              "Pure and undefiled religion before God the Father is this: to visit orphans and widows 
              in their affliction" - James 1:27
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
            {communityOutreach.map((ministry, index) => {
              const IconComponent = ministry.icon;
              return (
                <div key={index} className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300">
                  <div className="h-48 lg:h-56 overflow-hidden">
                    <img 
                      src={ministry.image}
                      alt={ministry.title}
                      className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                    />
                  </div>
                  
                  <div className="p-6 lg:p-8">
                    <div className="flex items-center space-x-3 mb-4">
                      <div className="w-12 h-12 bg-[#f17b21] rounded-full flex items-center justify-center">
                        <IconComponent className="w-6 h-6 text-white" />
                      </div>
                      <h3 className="text-xl lg:text-2xl font-bold text-gray-800 font-display">{ministry.title}</h3>
                    </div>
                    
                    <p className="text-gray-600 mb-6 leading-relaxed font-sans">
                      {ministry.description}
                    </p>

                    <div>
                      <h4 className="font-semibold text-gray-800 mb-3 font-sans">Current Needs:</h4>
                      <ul className="space-y-2 mb-6">
                        {ministry.needs.map((need, needIndex) => (
                          <li key={needIndex} className="flex items-start">
                            <span className="w-2 h-2 bg-[#f17b21] rounded-full mt-2 mr-3 flex-shrink-0"></span>
                            <span className="text-gray-600 font-sans text-sm lg:text-base">{need}</span>
                          </li>
                        ))}
                      </ul>
                      
                      <button 
                        onClick={() => window.location.href = 'mailto:contact@elbethel.online?subject=Volunteer Interest - ' + ministry.title}
                        className="bg-[#f17b21] hover:bg-[#d86919] text-white px-6 py-3 rounded-lg font-semibold transition-colors duration-300 font-sans w-full"
                      >
                        Get Involved
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Volunteer Call to Action */}
          <div className="mt-12 lg:mt-16 bg-gradient-to-r from-[#f17b21] to-[#ff9649] rounded-2xl p-6 lg:p-8 text-white text-center">
            <h3 className="text-2xl lg:text-3xl font-bold mb-4 font-display">Called to Serve</h3>
            <p className="text-lg opacity-90 mb-6 font-sans max-w-3xl mx-auto">
              "For we are God's handiwork, created in Christ Jesus to do good works, which God prepared 
              in advance for us to do." - Ephesians 2:10
            </p>
            <button 
              onClick={() => window.location.href = 'mailto:contact@elbethel.online?subject=Volunteer Opportunities'}
              className="bg-white text-[#f17b21] hover:bg-gray-100 px-8 py-4 rounded-lg font-semibold transition-colors duration-300 font-sans"
            >
              Join Our Outreach Team
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default OutreachPage;